import { createContext, type MouseEvent, type ReactNode, useContext, useEffect, useMemo, useState } from 'react';

interface NavigationValue {
  pathname: string;
  navigate: (pathname: string) => void;
}

const NavigationContext = createContext<NavigationValue | null>(null);

export function NavigationProvider({ children }: { children: ReactNode }) {
  const [pathname, setPathname] = useState(() => window.location.pathname);
  useEffect(() => {
    const onPop = () => setPathname(window.location.pathname);
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);
  const value = useMemo<NavigationValue>(() => ({
    pathname,
    navigate(next) {
      if (window.location.pathname !== next) window.history.pushState({}, '', next);
      setPathname(next);
      window.scrollTo({ top: 0, behavior: 'instant' });
    },
  }), [pathname]);
  return <NavigationContext.Provider value={value}>{children}</NavigationContext.Provider>;
}

export function useNavigation(): NavigationValue {
  const value = useContext(NavigationContext);
  if (!value) throw new Error('NavigationProvider is missing');
  return value;
}

export function Link({ href, children, className, onClick, ...rest }: { href: string; children: ReactNode; className?: string; onClick?: () => void } & Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, 'href' | 'onClick'>) {
  const { navigate } = useNavigation();
  function handleClick(event: MouseEvent<HTMLAnchorElement>) {
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
    event.preventDefault();
    onClick?.();
    navigate(href);
  }
  return <a href={href} className={className} onClick={handleClick} {...rest}>{children}</a>;
}
